import type { ChangelogResponse, ChangelogRepo } from "@xop/dashboard-shared";

/**
 * Typed fetch wrapper for the dashboard's /api/admin/changelog feed.
 * Threads the Authorization header from the calling component, mirroring
 * the Security / AI Providers panels' api.ts.
 */
export interface ChangelogParams {
  /** `YYYY-MM` — the month to show. */
  month?: string;
  /** Narrow to a single repo. */
  repo?: ChangelogRepo;
  /** "What's new since" mode: everything strictly after this `YYYY-MM-DD`. */
  since?: string;
  /** "What's new since" mode by desktop version (resolved → date server-side). */
  sinceVersion?: string;
}

async function request<T>(path: string, authHeader?: string): Promise<T> {
  const headers = new Headers();
  headers.set("Accept", "application/json");
  if (authHeader) headers.set("Authorization", authHeader);

  const res = await fetch(`/api/admin/changelog${path}`, { headers });
  const text = await res.text();
  if (!res.ok) {
    let body: unknown;
    try {
      body = JSON.parse(text);
    } catch {
      body = text;
    }
    const msg =
      typeof body === "object" && body !== null && "error" in body
        ? String((body as { error: unknown }).error)
        : `Request failed: ${res.status}`;
    throw new Error(msg);
  }
  return JSON.parse(text) as T;
}

export const changelogApi = {
  list: (params: ChangelogParams = {}, authHeader?: string) => {
    const qs = new URLSearchParams();
    if (params.month) qs.set("month", params.month);
    if (params.repo) qs.set("repo", params.repo);
    if (params.since) qs.set("since", params.since);
    if (params.sinceVersion) qs.set("sinceVersion", params.sinceVersion);
    const suffix = qs.toString() ? `?${qs.toString()}` : "";
    return request<ChangelogResponse>(suffix, authHeader);
  },
};
